import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, FileText, DollarSign } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { PageHeader } from '../../components/shared/PageHeader';
import { LoadingState } from '../../components/shared/LoadingState';
import { ErrorState } from '../../components/shared/ErrorState';
import { EmptyState } from '../../components/shared/EmptyState';
import { DataTable } from '../../components/shared/DataTable';
import { InvoiceStatusBadge } from './components/InvoiceStatusBadge';
import { useInvoices } from '../../core/hooks/useInvoices';
import { useAuth } from '../../core/contexts/AuthContext';
import type { Invoice } from '../../core/models/Invoice';
import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';

export const InvoicesPage: React.FC = () => {
  const navigate = useNavigate();
  const { organizationId } = useAuth();
  const { invoices, loading, error } = useInvoices({ organizationId });

  const columns: ColumnDef<Invoice>[] = [
    {
      accessorKey: 'number',
      header: 'Invoice #',
      cell: ({ row }) => (
        <div className="font-medium">{row.original.number}</div>
      ),
    },
    {
      accessorKey: 'date',
      header: 'Date',
      cell: ({ row }) => format(new Date(row.original.date), 'PP'),
    },
    {
      accessorKey: 'dueDate',
      header: 'Due Date',
      cell: ({ row }) => format(new Date(row.original.dueDate), 'PP'),
    },
    {
      accessorKey: 'total',
      header: 'Amount',
      cell: ({ row }) => (
        <span className="font-medium">${row.original.total.toFixed(2)}</span>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => <InvoiceStatusBadge status={row.original.status} />,
    },
    {
      id: 'actions',
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/invoices/${row.original.id}`)}
        >
          View
        </Button>
      ),
    },
  ];

  if (loading) {
    return <LoadingState message="Loading invoices..." />;
  }

  if (error) {
    return <ErrorState message="Failed to load invoices" />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Invoices"
        description="Manage invoices and billing"
        icon={DollarSign}
        actions={
          <Button onClick={() => navigate('/invoices/new')}>
            <Plus className="mr-2 h-4 w-4" />
            Create Invoice
          </Button>
        }
      />

      {invoices.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No invoices found"
          description="Create your first invoice to get started"
          action={{
            label: 'Create Invoice',
            onClick: () => navigate('/invoices/new'),
          }}
        />
      ) : (
        <DataTable
          columns={columns}
          data={invoices}
          searchKey="number"
          searchPlaceholder="Search invoices..."
        />
      )}
    </div>
  );
};
